/**
 * 用户中心首页
 */

import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useBrowserTitle, usePageVisibility } from 'hook/index';
import { service } from 'service';
import { GlobalLoading } from 'component/Loading/Loading';
import { DialogConfirm } from 'component/Dialog/DialogConfirm';
import { PlatformNameMap, isWechatMiniProgram, openH5InNewWebview } from 'util/index';
import styles from './UserCenter.module.scss';
import { NavItem } from './NavItem';
import { VipInfo } from './VipInfo';
import { MemberCard } from './MemberCard';
import { AnswerSummary, Props as SummaryData} from './AnswerSummary';
import { statistic, StatisticCategory, bridge } from 'util/index';

interface VipItem {
    isVip: boolean;
    expireTime?: string;
    isLifetime?: boolean;
}


interface BackendData {
    avatar: string;
    nickname: string;
    platform?: string;
    vipData: VipItem;
    answerSummary: SummaryData;
    bindData: {
        isBind: boolean;
        snBind: boolean;
        phoneBind: boolean;
        // 当前已绑定平台
        platform: string;
        xxzid: string;
    };
    // 是否在语音口算APP里，绑定了微信
  isBindWechatForApp: boolean;
}

interface State {
    // 是否加载中
    loading: boolean;
    // 是否已登录
    isLogin: boolean;
    avatar: string;
    nickname: string;
    platform?: string;
    vipData: BackendData['vipData'];
    answerSummary: BackendData['answerSummary'];
    bindData: BackendData['bindData'];
  isBindWechatForApp: BackendData['isBindWechatForApp'];
}

const defaultAnswerSummary = {
        appNum: 0,
        questionNum: 0,
        errorRate: '0%',
    };

const defaultBindData = {
        isBind: false,
        snBind: false,
        phoneBind: false,
        platform: '',
        xxzid: '',
};

// 视频课图标（内联 SVG 播放三角，避免依赖图标字体缺字）
function VideoIcon() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 5v14l11-7z" fill="#7a5cd0" />
        </svg>
    );
}

// 答题记录图标（内联 SVG：清单 + 对勾，表示答题历史/对错记录）
function RecordListIcon() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 6h9v2H4zM4 11h9v2H4zM4 16h6v2H4z" fill="#1d5bc5" />
            <path d="M15 15.5l2 2 4-4.2" stroke="#1d5bc5" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

// 功能练习表图标（内联 SVG：四宫格，表示技能/功能列表；配色区别于答题记录）
function SkillGridIcon() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3.5" y="3.5" width="7" height="7" rx="1.6" fill="#0d9488" />
            <rect x="13.5" y="3.5" width="7" height="7" rx="1.6" fill="#0d9488" />
            <rect x="3.5" y="13.5" width="7" height="7" rx="1.6" fill="#0d9488" />
            <rect x="13.5" y="13.5" width="7" height="7" rx="1.6" fill="#0d9488" />
        </svg>
    );
}

// 会员专享小锁（内联 SVG）
function LockMini() {
    return (
        <svg width="11" height="11" viewBox="0 0 24 24" style={{marginRight: '3px', verticalAlign: '-1px'}} aria-hidden="true">
            <rect x="5" y="11" width="14" height="9.5" rx="2" fill="#8a6100" />
            <path d="M8 11V7.8a4 4 0 0 1 8 0V11" stroke="#8a6100" strokeWidth="2" fill="none" />
        </svg>
    );
}

export default function UserCenter() {
  const isSelfApp = bridge.isInApp();
  // 「我的错题本」会员专享弹窗
  const [vipDialogVisible, setVipDialogVisible] = useState(false);
    const [data, setData] = useState<State>({
        loading: true,
        isLogin: false,
        avatar: '',
        nickname: '',
        vipData: {
            isVip: false,
        },
        answerSummary: defaultAnswerSummary,
        bindData: defaultBindData,
      isBindWechatForApp: false,
    });
    useEffect(function(){
        service.get(`/wx/user/info`)
            .then(function(out){
                console.log(out);
                if (out.status === 200 && out.data.status === 0) {
                    const realData: BackendData = out.data.data;
                    setData({
                        loading: false,
                        isLogin: !!realData.nickname,
                        avatar: realData.avatar,
                        nickname: realData.nickname,
                        platform: realData.platform,
                        vipData: realData.vipData || [],
                        answerSummary: realData.answerSummary || defaultAnswerSummary,
                        bindData: realData.bindData || defaultBindData,
                      isBindWechatForApp: realData.isBindWechatForApp,
                    });
                }
            })
            .catch(function(err: Error){
                setData({
                    loading: false,
                    isLogin: false,
                    avatar: '',
                    nickname: '',
                    vipData: {
                        isVip: false,
                    },
                    answerSummary: defaultAnswerSummary,
                    bindData: defaultBindData,
                  isBindWechatForApp: false,
                });
                alert(err.message);
            });
    },[]);
    useBrowserTitle('个人中心');
    const history = useHistory();
    const location = window.location;
    const documentHidden = usePageVisibility();
    const documentHiddenRef = useRef(documentHidden);
    useEffect(() => {
      // alert('visibility ' + documentVisible);
      if (documentHiddenRef.current && !documentHidden && isWechatMiniProgram()) {
        // 小程序里，重新回到个人中心时，刷新页面。获取最新会员信息
        window.location.reload();
      }
      documentHiddenRef.current = documentHidden;
    }, [documentHidden]);
    function showFeedback() {
        // location.href = '/mp/user/feedback';
      if (isWechatMiniProgram()) {
        openH5InNewWebview({
          url: '/mp/user/feedback',
          jumpMethod: 'navigateTo',
        });
      } else {
        location.href = '/mp/user/feedback';
      }
    }
    function showBindPage() {
        history.push('/mp/user/bindByCode');
    }
    function showUserPrivacyPage() {
      if (isWechatMiniProgram()) {
        openH5InNewWebview({
          url: '/mp/compliance/privacy',
          jumpMethod: 'navigateTo',
        });
      } else {
        history.push('/mp/compliance/privacy');
      }
    }
  function showUserServicePage() {
    if (isWechatMiniProgram()) {
      openH5InNewWebview({
        url: '/mp/compliance/userService',
        jumpMethod: 'navigateTo',
      });
    } else {
      history.push('/mp/compliance/userService');
    }
  }
  function showExerciseRecord() {
    if (isWechatMiniProgram()) {
      openH5InNewWebview({
        url: '/mp/record',
        jumpMethod: 'navigateTo',
      });
    } else {
      history.push('/mp/record');
    }
  }
    function showSkillList() {
        // 这里必须要走页面跳转，iOS下，pushState会导致报错
        if (!data.platform) {
            alert('请先绑定设备');
            return;
        }
        location.href = `/mp/skillList?platform=${encodeURIComponent(data.platform)}`;
    }

    function showLessonList() {
        // 精选视频课列表页（无参数默认进入第一个科目）
        if (isWechatMiniProgram()) {
            openH5InNewWebview({
                url: '/mp/lesson/list',
                jumpMethod: 'navigateTo',
            });
        } else {
            history.push('/mp/lesson/list');
        }
    }

    function subscribeWechatOfficialAccount() {
      // 小程序内用 navigateTo 开新 webview；微信 H5 等环境直接路由跳转
      if (isWechatMiniProgram()) {
        openH5InNewWebview({
          url: '/mp/promote/follow',
          jumpMethod: 'navigateTo',
        });
      } else {
        history.push('/mp/promote/follow');
      }
    }

    function gotoRecordCenter() {
      // 我的错题本独立页面（聚合全部技能错题）
      if (isWechatMiniProgram()) {
        openH5InNewWebview({ url: '/mp/record/myFailBook', jumpMethod: 'navigateTo' });
      } else {
        history.push('/mp/record/myFailBook');
      }
    }
    function gotoPayPage() {
      if (isWechatMiniProgram()) {
        openH5InNewWebview({ url: '/mp/user/pay', jumpMethod: 'navigateTo' });
      } else {
        history.push('/mp/user/pay');
      }
    }
    function showErrorBook() {
      // 错题本为会员专享：会员直接进入记录中心；非会员弹窗引导开通
      if (data.vipData.isVip) {
        gotoRecordCenter();
        return;
      }
      statistic({ category: StatisticCategory.UserCenter, action: '点击-错题本-未开通' });
      setVipDialogVisible(true);
    }
    function confirmOpenVip() {
      setVipDialogVisible(false);
      statistic({ category: StatisticCategory.UserCenter, action: '点击-错题本弹窗-开通会员' });
      gotoPayPage();
    }

    async function tryBindWeixinForApp() {
      let code = '';
      try {
        const res: any = await bridge.invokeWechatAuthForApp();
        code = res.code;
      } catch (err) {
        code = '';
        console.error('tryBindWeixinForApp', err);
      }
      if (!code) {
        alert('获取微信授权异常');
        return;
      }
      try {
        const out = await service.get(`/native/passport/wechatLoginForNativeApp?code=${encodeURIComponent(code)}`);
        if (out.status === 200 && out.data.status === 0) {
          const body: any = out.data.data;
          if (body.userId && body.xxzId) {
            bridge.setIds(body.userId, body.xxzId);
            alert('微信绑定成功');
            setTimeout(() => {
              window.location.reload();
            }, 200);
          } else {
            throw new Error('服务异常，未获取到用户ID');
          }
        } else {
          throw new Error('授权服务异常，请稍后再试');
        }
      } catch (err) {
        alert(err.message);
      }
    }

    // 设备绑定结果
    let bindResultText = '';
    // 头像下方的设备信息行，形如「已绑定小度 · 小学僮-2****」
    let headBindText = '';
    if (data.bindData.isBind) {
        bindResultText = '已绑定';
        if (PlatformNameMap[data.bindData.platform]) {
            bindResultText += PlatformNameMap[data.bindData.platform];
        }
        let xxzId = `${data.bindData.xxzid || ''}`;
        if (xxzId) {
            xxzId = xxzId.substring(0, xxzId.length - 3) + '****';
        }
        bindResultText += `-${xxzId}`;

        const platName = PlatformNameMap[data.bindData.platform] || '';
        headBindText = `已绑定${platName} · 小学僮-${xxzId}`;
    }
    let bindWechatInApp: JSX.Element | false = false;
    if (isSelfApp) {
      if (data.isBindWechatForApp) {
        // 已绑定
        bindWechatInApp = (
          <NavItem key="bindWechat" titleIconName="xt-icon-product" titleIconStyle={{color: '#9509b1'}} iconBg="#f3e8fb" title="绑定微信" subText='已绑定微信'/>
        );
      } else {
        bindWechatInApp = (
          <NavItem key="bindWechat" titleIconName="xt-icon-product" titleIconStyle={{color: '#9509b1'}} iconBg="#f3e8fb" title="绑定微信" subText=''  onClick={tryBindWeixinForApp}/>
        );
      }
    }

    // 「我的学习」分组：错题本 / 答题记录 / 精选视频课 / 功能练习表
    const learnItems: (JSX.Element | false)[] = [
      <NavItem key="errorBook" titleIconName="xt-icon-feeds" titleIconStyle={{color: '#d85a30'}} iconBg="#fdecea" title="我的错题本"
               subText={!data.vipData.isVip ? (<span className={styles.vipOnlyTag}><LockMini/>会员专享</span>) : ''} onClick={showErrorBook}/>,
      <NavItem key="record" titleIcon={<RecordListIcon/>} iconBg="#e6f1fb" title="答题记录" onClick={showExerciseRecord}/>,
      <NavItem key="lesson" titleIcon={<VideoIcon/>} iconBg="#f0ebff" title="精选视频课" onClick={showLessonList}/>,
      !isSelfApp &&
      <NavItem key="skill" titleIcon={<SkillGridIcon/>} iconBg="#e5f6f4" title="功能练习表" onClick={showSkillList}/>,
    ];
    // 「账号与设置」分组：绑定设备 / 关注公众号 /（App）绑定微信 / 用户反馈
    const accountItems: (JSX.Element | false)[] = [
      !isSelfApp &&
      <NavItem key="bindDevice" titleIconName="xt-icon-product" titleIconStyle={{color: '#f15b41'}} iconBg="#fdecea" title="绑定设备" subText={bindResultText} onClick={showBindPage}/>,
      !isSelfApp &&
      <NavItem key="follow" titleIconName="xt-icon-mobile-phone" titleIconStyle={{color: '#1aad19'}} iconBg="#e7f5ee" title="关注公众号" subText={'接收错题/会员通知'} onClick={subscribeWechatOfficialAccount}/>,
      bindWechatInApp,
      <NavItem key="feedback" titleIconName="xt-icon-feeds" titleIconStyle={{color: '#a9920a'}} iconBg="#fcf7d9" title="用户反馈" onClick={showFeedback}/>,
    ];
    function renderGroup(items: (JSX.Element | false)[]) {
      const visible = items.filter(Boolean) as JSX.Element[];
      return visible.map(function(node, i){
        return React.cloneElement(node, { showTopBorder: i > 0 });
      });
    }

    return (
        <div className={styles.page}>
            <div className={styles.profileCard}>
                <div className={`${styles.head} ${styles.headWithCard}`}>
                    <img src={data.avatar} className={styles.avatar} />
                    { !data.loading &&
                    <div className={styles.headRight}>
                        <div className={styles.nameRow}>
                            <span className={styles.nickname}>{data.nickname || '小学僮用户'}</span>
                            <VipInfo isVip={data.vipData.isVip} expireTime={data.vipData.expireTime}/>
                        </div>
                        {headBindText && <div className={styles.bindInfo}>{headBindText}</div>}
                    </div>
                    }
                </div>
                { !data.loading &&
                  <MemberCard
                    isVip={data.vipData.isVip}
                    expireTime={data.vipData.expireTime}
                    isLifetime={data.vipData.isLifetime}
                  />
                }
            </div>

            <AnswerSummary {...data.answerSummary} />

            <div className={styles.sectLabel}>
              <span className={styles.sectBar} style={{background: '#378ADD'}} />
              <span className={styles.sectText}>我的学习</span>
            </div>
            <div className={styles.navGroup}>
              {renderGroup(learnItems)}
            </div>

            <div className={styles.sectLabel}>
              <span className={styles.sectBar} style={{background: '#9b59b6'}} />
              <span className={styles.sectText}>账号与设置</span>
            </div>
            <div className={styles.navGroup}>
              {renderGroup(accountItems)}
            </div>

            <div className={styles.footer}>
              <span className={styles.footerLink} onClick={showUserPrivacyPage}>用户隐私政策</span>
              <span className={styles.footerDot}>·</span>
              <span className={styles.footerLink} onClick={showUserServicePage}>用户服务协议</span>
            </div>
            <DialogConfirm
                visible={vipDialogVisible}
                title="会员专享"
                okBtnText="开通会员"
                cancelBtnText="取消"
                onOk={confirmOpenVip}
                onCancel={() => setVipDialogVisible(false)}
                onMaskClick={() => setVipDialogVisible(false)}
            >
                错题本是会员专享功能，开通会员后可查看孩子的错题集、智能出题并免除广告。
            </DialogConfirm>
            <GlobalLoading visible={data.loading}/>
        </div>
    );
}
